import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { ShieldX, ArrowLeft } from 'lucide-react'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: 'ADMIN' | 'OPERATOR' | 'VIEWER'
  requiredCapability?: string
  fallbackPath?: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  requiredCapability,
  fallbackPath = '/login' 
}) => {
  const { isAuthenticated, isLoading, user, can } = useAuth()
  const location = useLocation()
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">{t('checking_auth')}</div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />
  }

  if ((requiredRole || requiredCapability) && user) {
    const hasRequiredRole = !requiredRole || checkRoleAccess(user.role, requiredRole)
    const hasRequiredCapability = !requiredCapability || can(requiredCapability)
    
    if (!hasRequiredRole || !hasRequiredCapability) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4">
                <ShieldX size={28} className="text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">{t('access_denied')}</h2>
              <p className="text-gray-600 mb-4">
                {t('access_denied_message')}
              </p>
              <div className="text-sm text-gray-500 mb-6">
                {requiredRole && <>{t('required_role')}: <span className="font-medium text-red-600">{requiredRole}</span><br/></>}
                {requiredCapability && <>{t('required_capability')}: <span className="font-medium text-red-600">{requiredCapability}</span><br/></>}
                {t('your_role')}: <span className="font-medium text-blue-600">{user.role}</span>
              </div>
              <button
                onClick={() => window.history.back()}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 inline-flex items-center gap-2"
              >
                <ArrowLeft size={16} />
                {t('go_back')}
              </button>
            </div>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}

const checkRoleAccess = (userRole: string, requiredRole: string): boolean => {
  const roleHierarchy = {
    'ADMIN': 3,
    'OPERATOR': 2, 
    'VIEWER': 1
  }

  const userLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 0
  const requiredLevel = roleHierarchy[requiredRole as keyof typeof roleHierarchy] || 0

  return userLevel >= requiredLevel
}

export default ProtectedRoute
